import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import defaultLogo from "../../assets/super_admin/logo/super_admin.png";

export default function SuperAdminHeader({ onToggle }) {
  const [userName, setUserName] = useState("");
  const [avatarSrc, setAvatarSrc] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [role, setRole] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    try {
      setUserName(localStorage.getItem("name") || "Super Admin");
      setRole(localStorage.getItem("role") || "owner");
      const avatar = localStorage.getItem("avatar");
      if (avatar) setAvatarSrc(avatar);
    } catch (e) {
      console.warn("Header init error", e);
    }
  }, []);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/super_admin/login";
  };

  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 72,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 1.25, sm: 2.5, md: 4 },
        background: "linear-gradient(100deg, #ffffff 0%, #f7fcfd 58%, #e7f8f6 100%)",
        borderBottom: "1px solid rgba(8,145,178,0.2)",
        borderTop: "3px solid #0891b2",
        zIndex: 1400,
        boxShadow: "0 12px 34px rgba(16,42,67,0.15)",
        backdropFilter: "saturate(130%) blur(14px)",
      }}
    >
      {/* Left side - toggle + logo */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1.25 }, minWidth: 0 }}>
        <IconButton
          onClick={() => typeof onToggle === "function" && onToggle()}
          sx={{
            color: "#102a43",
            bgcolor: "rgba(37,99,235,0.1)",
            border: "1px solid rgba(37,99,235,0.18)",
            borderRadius: 1.5,
            transition: "transform .2s ease, background .2s ease",
            "&:hover": {
              bgcolor: "rgba(8,145,178,0.14)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Avatar
            src={defaultLogo}
            alt="FeesBook logo"
            variant="rounded"
            sx={{
              width: { xs: 42, sm: 48 },
              height: { xs: 42, sm: 48 },
              borderRadius: 1.5,
              border: "1px solid rgba(8,145,178,0.22)",
              boxShadow: "0 8px 18px rgba(23,32,51,0.12)",
            }}
          />
          <Typography
            sx={{
              display: { xs: "block", sm: "block" },
              color: "#123a5a",
              fontSize: { xs: "1.35rem", sm: "1.85rem" },
              fontWeight: 900,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            FeesBook
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Right side - user info */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
        {!isMobile && (
          <Typography
            variant="body2"
            sx={{ fontWeight: 900, mr: 0.5, maxWidth: 160, color: "#102a43", fontSize: 13 }}
            noWrap
          >
            {userName}
          </Typography>
        )}

        <Tooltip title="Account">
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              p: 0.45,
              border: "1px solid rgba(8,145,178,0.2)",
              bgcolor: "rgba(255,255,255,0.82)",
              boxShadow: "0 6px 16px rgba(16,42,67,0.08)",
            }}
          >
            <Avatar
              src={avatarSrc || undefined}
              alt={userName}
              sx={{ width: 40, height: 40, bgcolor: "#2563eb", border: "2px solid #fff" }}
            >
              {!avatarSrc && <AccountCircleIcon />}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              minWidth: 190,
              p: 0,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(78,102,135,0.16)",
              boxShadow: "0 18px 46px rgba(23,32,51,0.16)",
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {userName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary" }}
              noWrap
            >
              {role}
            </Typography>
          </Box>

          <Divider sx={{ my: 0 }} />

          <MenuItem sx={{ gap: 1 }}>
            <AccountCircleIcon fontSize="small" />
            <Typography variant="body2">Profile</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleMenuClose();
              handleLogout();
            }}
            sx={{ gap: 1 }}
          >
            <LogoutIcon fontSize="small" />
            <Typography variant="body2">Logout</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
